import React from 'react';
import classNames from 'classnames';

// Embed found → videos found → expert videos found → JSON-LD found: the
// basic checks a product page shows, read off its last PDP analysis (API
// lib/pdpPageChecks, on a PDP URL row as `checks` and on an Autopilot page
// as `checks`). Embed = our embed script (e2.js / e2s.js), shown only once
// found; videos = our videos on the page, however they got there, shown
// after every video the page holds; JSON-LD = our markup the page serves.
// The google tag (gt.js) is not part of the strip — it gets the separate
// Needs tag / Tag installed chip (PdpTagPill, or the Autopilot row's own),
// and the Shopify pixel its own beside it (PdpPixelPill).
export interface PdpChecks {
    analyzed_at: string | null;
    // Our embed script(s) / google tag seen on the page. `checked` is false
    // when the last analysis predates this check.
    script: { checked: boolean; found: boolean; embeds: number; google_tag: boolean };
    // The google tag specifically: on the page per the analysis, or heard
    // from on this host per its own pings.
    tag?: { seen: boolean; on_page: boolean; last_seen_at: string | null } | null;
    // The brand's Shopify custom pixel on this page's host; null (no chip)
    // when it can't cover the page — no pixel, network plan, retailer host.
    pixel?: { seen: boolean; last_seen_at: string | null } | null;
    // Our videos the analysis found on the page vs the videos linked to it.
    videos: { checked: boolean; found: number; linked: number; on_page: number };
    // Our JSON-LD the page serves (a VideoObject per video, each about the
    // page's product) vs what we have generated for it. generated counts
    // VideoObjects (linked videos' + the page's other videos');
    // product_generated = the page markup block exists; product = a node on
    // the page names our product id.
    json_ld: { checked: boolean; found: number; product: boolean; generated: number; product_generated?: boolean };
}

export type PdpCheckTone = 'good' | 'warn' | 'neutral';

export interface PdpCheckStep {
    key: 'script' | 'page_videos' | 'videos' | 'json_ld';
    label: string;
    tone: PdpCheckTone;
    title: string;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

// The three steps, worded for the pill and its tooltip. Pure — the video
// settings modal and the Autopilot rows read the same rules.
export const pdpCheckSteps = (checks: PdpChecks | null | undefined): PdpCheckStep[] => {
    if (!checks) return [];
    const { script, videos, json_ld: jsonLd } = checks;

    // Our embed script, only once the analysis found it — a page without it
    // is the norm, and the tag chip covers the script-tag side.
    const embedStep: PdpCheckStep | null =
        script.checked && script.embeds > 0
            ? { key: 'script', label: 'Embed found', tone: 'good', title: `The last analysis found ${plural(script.embeds, 'embed script', 'embed scripts')} on the page.` }
            : null;

    // Every video on the page (the retailer's included), then ours.
    let pageVideosStep: PdpCheckStep;
    if (!videos.checked) {
        pageVideosStep = { key: 'page_videos', label: 'Videos not checked', tone: 'neutral', title: 'Run a PDP analysis to look for videos on the page.' };
    } else if (videos.on_page > 0) {
        pageVideosStep = {
            key: 'page_videos',
            label: `${plural(videos.on_page, 'video', 'videos')} found`,
            tone: 'good',
            title: `The last analysis saw ${plural(videos.on_page, 'video', 'videos')} on the page, ours and the retailer's own.`,
        };
    } else {
        pageVideosStep = { key: 'page_videos', label: 'No videos found', tone: 'neutral', title: 'The last analysis saw no video on the page.' };
    }

    let videosStep: PdpCheckStep;
    if (!videos.checked) {
        videosStep = { key: 'videos', label: 'Expert videos not checked', tone: 'neutral', title: 'Run a PDP analysis to look for our expert videos on the page.' };
    } else if (videos.found > 0) {
        videosStep = {
            key: 'videos',
            label: `${plural(videos.found, 'expert video', 'expert videos')} found`,
            tone: 'good',
            title: `${videos.found} of ${plural(videos.linked, 'linked expert video', 'linked expert videos')} found on the page.`,
        };
    } else if (videos.linked === 0) {
        videosStep = { key: 'videos', label: 'No expert video yet', tone: 'neutral', title: 'None of our expert videos is on this page yet — add one from the video\'s settings, or place the embed script on the page.' };
    } else {
        videosStep = {
            key: 'videos',
            label: 'Expert videos not found',
            tone: 'warn',
            title: `${plural(videos.linked, 'linked expert video', 'linked expert videos')}, none found on the page by the last analysis.`,
        };
    }

    let jsonLdStep: PdpCheckStep;
    if (!jsonLd.checked) {
        jsonLdStep = { key: 'json_ld', label: 'JSON-LD not checked', tone: 'neutral', title: 'Run a PDP analysis to check the page for our JSON-LD.' };
    } else if (jsonLd.found > 0 || jsonLd.product) {
        const parts: string[] = [];
        if (jsonLd.found > 0) parts.push(plural(jsonLd.found, 'Video Object', 'Video Objects'));
        if (jsonLd.product) parts.push('product reference');
        jsonLdStep = { key: 'json_ld', label: 'JSON-LD found', tone: 'good', title: `Our markup on the page: ${parts.join(' · ')}.` };
    } else if (jsonLd.generated === 0 && !jsonLd.product_generated) {
        jsonLdStep = { key: 'json_ld', label: 'JSON-LD not generated', tone: 'neutral', title: 'No JSON-LD generated for this page yet — generate it below, then put it on the page.' };
    } else {
        const parts: string[] = [];
        if (jsonLd.generated > 0) parts.push(plural(jsonLd.generated, 'Video Object', 'Video Objects'));
        if (jsonLd.product_generated) parts.push('page markup');
        jsonLdStep = {
            key: 'json_ld',
            label: 'JSON-LD not found',
            tone: 'warn',
            title: `Generated (${parts.join(' · ')}), not on the page yet — copy the markup below onto the page.`,
        };
    }
    return [...(embedStep ? [embedStep] : []), pageVideosStep, videosStep, jsonLdStep];
};

const toneCls: Record<PdpCheckTone, string> = {
    good: 'text-up border-up/40',
    warn: 'text-accent-text border-accent/40',
    neutral: 'text-muted border-border-token',
};

const dotCls: Record<PdpCheckTone, string> = {
    good: 'bg-up',
    warn: 'bg-accent',
    neutral: 'bg-border-token',
};

interface Props {
    checks: PdpChecks | null | undefined;
    className?: string;
    // Smaller pills for a list row.
    compact?: boolean;
}

export function PdpChecksStrip({ checks, className, compact = false }: Props) {
    const steps = pdpCheckSteps(checks);
    const pillCls = classNames(
        'inline-flex items-center gap-[5px] font-semibold border rounded-[6px] whitespace-nowrap',
        compact ? 'text-[10.5px] px-[6px] py-[1px]' : 'text-[11px] px-2 py-[2px]'
    );
    if (!steps.length) {
        return (
            <span
                className={classNames('inline-flex items-center gap-[6px]', className)}
                title="The basic checks come from the page's PDP analysis: our embed on it, our videos on it, our JSON-LD on it."
            >
                <span className={classNames(pillCls, toneCls.neutral)}>Not analyzed yet</span>
            </span>
        );
    }
    return (
        <span className={classNames('inline-flex items-center gap-[6px] flex-wrap', className)} aria-label="Basic page checks">
            {steps.map((step, i) => (
                <React.Fragment key={step.key}>
                    {i > 0 ? (
                        <span className="text-muted text-[11px]" aria-hidden>
                            →
                        </span>
                    ) : null}
                    <span className={classNames(pillCls, toneCls[step.tone])} title={step.title}>
                        <span className={classNames('inline-block w-[6px] h-[6px] rounded-full', dotCls[step.tone])} aria-hidden />
                        {step.label}
                    </span>
                </React.Fragment>
            ))}
        </span>
    );
}

// The google tag on its own — the same Needs tag / Tag installed state the Autopilot
// row shows, for the Product Pages row: seen when the analysis found
// gt.js on the page or the tag has reported from this host.
export function PdpTagPill({ checks, className, compact = false }: Props) {
    if (!checks || !checks.tag) return null;
    const { tag } = checks;
    const pillCls = classNames(
        'inline-flex items-center gap-[5px] font-semibold border rounded-[6px] whitespace-nowrap',
        compact ? 'text-[10.5px] px-[6px] py-[1px]' : 'text-[11px] px-2 py-[2px]',
        className
    );
    if (tag.seen) {
        const how = tag.on_page
            ? 'The script tag (gt.js) is on the page.'
            : `The script tag (gt.js) has reported from this site${tag.last_seen_at ? `, last on ${new Date(tag.last_seen_at).toLocaleDateString()}` : ''}.`;
        return (
            <span className={classNames(pillCls, toneCls.good)} title={how}>
                <span className={classNames('inline-block w-[6px] h-[6px] rounded-full', dotCls.good)} aria-hidden />
                Tag installed
            </span>
        );
    }
    return (
        <span
            className={classNames(pillCls, toneCls.warn)}
            title="The script tag (gt.js) has not been seen on this page — install it from Settings › Onboarding. Until then the page is measured by analysis and Consumer Voice only."
        >
            <span className={classNames('inline-block w-[6px] h-[6px] rounded-full', dotCls.warn)} aria-hidden />
            Needs tag
        </span>
    );
}

// The brand's Shopify custom pixel on this page's host (Settings ›
// Onboarding): installed once it has reported from the host. No chip when
// the pixel can't cover the page (checks.pixel null).
export function PdpPixelPill({ checks, className, compact = false }: Props) {
    if (!checks || !checks.pixel) return null;
    const { pixel } = checks;
    const pillCls = classNames(
        'inline-flex items-center gap-[5px] font-semibold border rounded-[6px] whitespace-nowrap',
        compact ? 'text-[10.5px] px-[6px] py-[1px]' : 'text-[11px] px-2 py-[2px]',
        className
    );
    if (pixel.seen) {
        return (
            <span
                className={classNames(pillCls, toneCls.good)}
                title={`The TDC Shopify pixel has reported from this site${pixel.last_seen_at ? `, last on ${new Date(pixel.last_seen_at).toLocaleDateString()}` : ''}.`}
            >
                <span className={classNames('inline-block w-[6px] h-[6px] rounded-full', dotCls.good)} aria-hidden />
                TDC Shopify Pixel
            </span>
        );
    }
    return (
        <span
            className={classNames(pillCls, toneCls.warn)}
            title="The TDC Shopify pixel has not reported from this site yet — add it in Shopify from Settings › Onboarding. Until then Shopify checkouts and orders are not tracked."
        >
            <span className={classNames('inline-block w-[6px] h-[6px] rounded-full', dotCls.warn)} aria-hidden />
            Needs Shopify Pixel
        </span>
    );
}
